import {
  WorkspaceLayout,
  WorkspaceLayoutNode,
  WorkspacePane,
  WorkspaceSplit,
  TabState,
  SplitDirection,
  MIN_PANE_SIZE_PCT,
  DEFAULT_SPLIT_RATIO,
  MAX_PANES,
  LayoutMutationResult,
} from '@arctek/sysml-types';

let idCounter = 0;
export function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${idCounter++}`;
}

export function createDefaultLayout(): WorkspaceLayout {
  const paneId = generateId('pane');
  return {
    version: 1,
    activePaneId: paneId,
    root: {
      type: 'pane',
      id: paneId,
      tabs: [],
      activeTabId: undefined,
    },
  };
}

function getAllPanes(node: WorkspaceLayoutNode): WorkspacePane[] {
  if (node.type === 'pane') {
    return [node];
  }
  return [...getAllPanes(node.children[0]), ...getAllPanes(node.children[1])];
}

function getAllIds(node: WorkspaceLayoutNode): Set<string> {
  if (node.type === 'pane') {
    return new Set([node.id]);
  }
  const leftIds = getAllIds(node.children[0]);
  const rightIds = getAllIds(node.children[1]);
  return new Set([node.id, ...leftIds, ...rightIds]);
}

export function validateLayout(layout: WorkspaceLayout): string | null {
  if (!layout.root) {
    return 'Root node is null or undefined';
  }

  const encounteredIds = new Set<string>();
  let hasDuplicateIds = false;
  const collectIds = (node: WorkspaceLayoutNode): void => {
    if (encounteredIds.has(node.id)) {
      hasDuplicateIds = true;
      return;
    }
    encounteredIds.add(node.id);
    if (node.type === 'split') {
      collectIds(node.children[0]);
      collectIds(node.children[1]);
    }
  };
  collectIds(layout.root);
  if (hasDuplicateIds) {
    return 'Duplicate node IDs detected';
  }

  const panes = getAllPanes(layout.root);
  if (panes.length > MAX_PANES) {
    return `Exceeded maximum panes (${MAX_PANES})`;
  }

  const paneIds = new Set(panes.map(p => p.id));
  if (!paneIds.has(layout.activePaneId)) {
    return `activePaneId ${layout.activePaneId} does not reference an existing pane`;
  }

  for (const pane of panes) {
    if (pane.activeTabId && !pane.tabs.find(t => t.id === pane.activeTabId)) {
      return `Pane ${pane.id} activeTabId ${pane.activeTabId} does not exist in tabs`;
    }
  }

  function validateNode(node: WorkspaceLayoutNode): string | null {
    if (node.type === 'split') {
      if (!node.children || node.children.length !== 2) {
        return `Split node ${node.id} does not have exactly 2 children`;
      }
      if (!node.children[0] || !node.children[1]) {
        return `Split node ${node.id} has null children`;
      }
      const sum = node.sizes[0] + node.sizes[1];
      if (Math.abs(sum - 100) > 0.5) {
        return `Split node ${node.id} sizes sum to ${sum}, not 100`;
      }
      const leftError = validateNode(node.children[0]);
      if (leftError) return leftError;
      const rightError = validateNode(node.children[1]);
      if (rightError) return rightError;
    }
    return null;
  }

  return validateNode(layout.root);
}

function findPaneById(node: WorkspaceLayoutNode, paneId: string): WorkspacePane | null {
  if (node.type === 'pane') {
    return node.id === paneId ? node : null;
  }
  return findPaneById(node.children[0], paneId) || findPaneById(node.children[1], paneId);
}

function cloneNode(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
  if (node.type === 'pane') {
    return {
      ...node,
      tabs: [...node.tabs],
    };
  }
  return {
    ...node,
    children: [cloneNode(node.children[0]), cloneNode(node.children[1])],
    sizes: [...node.sizes] as [number, number],
  };
}

export function splitPane(
  layout: WorkspaceLayout,
  paneId: string,
  direction: SplitDirection
): LayoutMutationResult {
  const targetPane = findPaneById(layout.root, paneId);
  if (!targetPane) {
    return { success: false, error: `Pane ${paneId} not found` };
  }

  const panes = getAllPanes(layout.root);
  if (panes.length >= MAX_PANES) {
    return { success: false, error: `Cannot split: maximum ${MAX_PANES} panes reached` };
  }

  const newPaneId = generateId('pane');
  const newPane: WorkspacePane = {
    type: 'pane',
    id: newPaneId,
    tabs: [],
    activeTabId: undefined,
  };

  const newSplit: WorkspaceSplit = {
    type: 'split',
    id: generateId('split'),
    direction,
    children: [cloneNode(targetPane), newPane],
    sizes: [...DEFAULT_SPLIT_RATIO],
  };

  function replaceNode(node: WorkspaceLayoutNode, targetId: string, replacement: WorkspaceLayoutNode): WorkspaceLayoutNode {
    if (node.type === 'pane') {
      return node.id === targetId ? replacement : node;
    }
    if (node.id === targetId) {
      return replacement;
    }
    return {
      ...node,
      children: [
        replaceNode(node.children[0], targetId, replacement),
        replaceNode(node.children[1], targetId, replacement),
      ],
    };
  }

  let newRoot = layout.root.id === paneId ? newSplit : replaceNode(layout.root, paneId, newSplit);

  const newLayout: WorkspaceLayout = {
    ...layout,
    activePaneId: newPaneId,
    root: newRoot,
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}

export function closePane(layout: WorkspaceLayout, paneId: string): LayoutMutationResult {
  const panes = getAllPanes(layout.root);
  if (panes.length === 1) {
    return { success: false, error: 'Cannot close the only pane' };
  }

  let parentSplitId: string | null = null;
  let sibling: WorkspaceLayoutNode | null = null;

  function findParentAndSibling(node: WorkspaceLayoutNode): boolean {
    if (node.type === 'pane') {
      return node.id === paneId;
    }
    if (findParentAndSibling(node.children[0])) {
      parentSplitId = node.id;
      sibling = node.children[1];
      return true;
    }
    if (findParentAndSibling(node.children[1])) {
      parentSplitId = node.id;
      sibling = node.children[0];
      return true;
    }
    return false;
  }

  findParentAndSibling(layout.root);

  if (!sibling) {
    return { success: false, error: `Cannot find sibling for pane ${paneId}` };
  }

  let newRoot: WorkspaceLayoutNode;
  if (!parentSplitId) {
    // Root is the pane itself, replace with sibling
    newRoot = sibling;
  } else {
    function replaceSplit(node: WorkspaceLayoutNode, splitId: string, replacement: WorkspaceLayoutNode): WorkspaceLayoutNode {
      if (node.type === 'pane') {
        return node;
      }
      if (node.id === splitId) {
        return replacement;
      }
      return {
        ...node,
        children: [
          replaceSplit(node.children[0], splitId, replacement),
          replaceSplit(node.children[1], splitId, replacement),
        ] as [WorkspaceLayoutNode, WorkspaceLayoutNode],
      };
    }
    newRoot = replaceSplit(layout.root, parentSplitId, sibling);
  }

  const remainingPanes = getAllPanes(newRoot);
  const newActivePaneId = layout.activePaneId === paneId
    ? (remainingPanes.find(p => p.id !== paneId)?.id || remainingPanes[0]?.id || '')
    : layout.activePaneId;

  const newLayout: WorkspaceLayout = {
    ...layout,
    activePaneId: newActivePaneId,
    root: newRoot,
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}

export function moveTab(
  layout: WorkspaceLayout,
  tabId: string,
  targetPaneIdParam: string,
  index?: number
): LayoutMutationResult {
  const allPanes = getAllPanes(layout.root);
  const sourcePane = allPanes.find(p => p.tabs.some(t => t.id === tabId));
  const targetPane = allPanes.find(p => p.id === targetPaneIdParam);

  if (!sourcePane || !targetPane) {
    return { success: false, error: `Tab ${tabId} or pane ${targetPaneIdParam} not found` };
  }

  const tab = sourcePane.tabs.find(t => t.id === tabId);
  if (!tab) {
    return { success: false, error: `Tab ${tabId} not found in source pane` };
  }
  
  const sourcePaneId = sourcePane.id;
  const targetPaneId = targetPane.id;

  if (sourcePaneId === targetPaneId) {
    const currentIndex = sourcePane.tabs.findIndex(t => t.id === tabId);
    if (currentIndex < 0) {
      return { success: false, error: `Tab ${tabId} not found in source pane` };
    }

    const reorderedTabs = [...sourcePane.tabs];
    const [movedTab] = reorderedTabs.splice(currentIndex, 1);
    const rawInsertIndex = index ?? reorderedTabs.length;
    const clampedInsertIndex = Math.max(0, Math.min(rawInsertIndex, reorderedTabs.length));
    reorderedTabs.splice(clampedInsertIndex, 0, movedTab);

    function updatePaneTabs(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
      if (node.type === 'pane' && node.id === sourcePaneId) {
        return { ...node, tabs: reorderedTabs, activeTabId: tabId };
      }
      if (node.type === 'split') {
        return {
          ...node,
          children: [updatePaneTabs(node.children[0]), updatePaneTabs(node.children[1])] as [WorkspaceLayoutNode, WorkspaceLayoutNode],
        };
      }
      return node;
    }

    const reorderedLayout: WorkspaceLayout = {
      ...layout,
      activePaneId: targetPaneId,
      root: updatePaneTabs(layout.root),
    };

    const reorderedError = validateLayout(reorderedLayout);
    if (reorderedError) {
      return { success: false, error: reorderedError };
    }

    return { success: true, layout: reorderedLayout };
  }
  
  const newSourceTabs = sourcePane.tabs.filter(t => t.id !== tabId);
  const newSourceActiveTabId = sourcePane.activeTabId === tabId
    ? newSourceTabs[0]?.id
    : sourcePane.activeTabId;

  const insertIndex = index !== undefined ? index : targetPane.tabs.length;
  const newTargetTabs = [...targetPane.tabs];
  newTargetTabs.splice(insertIndex, 0, tab);

  function updatePanes(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
    if (node.type === 'pane') {
      if (node.id === sourcePaneId) {
        return { ...node, tabs: newSourceTabs, activeTabId: newSourceActiveTabId };
      }
      if (node.id === targetPaneId) {
        return { ...node, tabs: newTargetTabs, activeTabId: tabId };
      }
      return node;
    }
    return {
      ...node,
      children: [updatePanes(node.children[0]), updatePanes(node.children[1])] as [WorkspaceLayoutNode, WorkspaceLayoutNode],
    };
  }

  const newLayout: WorkspaceLayout = {
    ...layout,
    activePaneId: targetPaneId,
    root: updatePanes(layout.root),
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}

export function openTab(
  layout: WorkspaceLayout,
  config: TabState,
  targetPaneId?: string,
  forceDuplicate: boolean = false
): LayoutMutationResult {
  const paneId = targetPaneId || layout.activePaneId;
  const allPanes = getAllPanes(layout.root);
  const targetPane = allPanes.find(p => p.id === paneId);

  if (!targetPane) {
    return { success: false, error: `Pane ${paneId} not found` };
  }

  if (!forceDuplicate && config.documentId && config.viewType) {
    const existingTab = targetPane.tabs.find(
      t => t.documentId === config.documentId && t.viewType === config.viewType
    );
    if (existingTab) {
      const existingTabId = existingTab.id;
      function updateActiveTab(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
        if (node.type === 'pane' && node.id === paneId) {
          return { ...node, activeTabId: existingTabId };
        }
        if (node.type === 'split') {
          return {
            ...node,
            children: [updateActiveTab(node.children[0]), updateActiveTab(node.children[1])],
          };
        }
        return node;
      }

      return {
        success: true,
        layout: {
          ...layout,
          activePaneId: paneId,
          root: updateActiveTab(layout.root),
        },
      };
    }
  }

  const newTab: TabState = {
    ...config,
    id: config.id || generateId('tab'),
  };

  function addTab(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
    if (node.type === 'pane' && node.id === paneId) {
      return {
        ...node,
        tabs: [...node.tabs, newTab],
        activeTabId: newTab.id,
      };
    }
    if (node.type === 'split') {
      return {
        ...node,
        children: [addTab(node.children[0]), addTab(node.children[1])],
      };
    }
    return node;
  }

  const newLayout: WorkspaceLayout = {
    ...layout,
    activePaneId: paneId,
    root: addTab(layout.root),
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}

export function closeTab(layout: WorkspaceLayout, tabId: string): LayoutMutationResult {
  const allPanes = getAllPanes(layout.root);
  const sourcePane = allPanes.find(p => p.tabs.some(t => t.id === tabId));

  if (!sourcePane) {
    return { success: false, error: `Tab ${tabId} not found` };
  }

  const sourcePaneId = sourcePane.id;
  const tabIndex = sourcePane.tabs.findIndex(t => t.id === tabId);
  const newTabs = sourcePane.tabs.filter(t => t.id !== tabId);

  let newActiveTabId = sourcePane.activeTabId;
  if (sourcePane.activeTabId === tabId) {
    if (newTabs.length > 0) {
      const nextIndex = Math.min(tabIndex, newTabs.length - 1);
      newActiveTabId = newTabs[nextIndex]?.id;
    } else {
      newActiveTabId = undefined;
    }
  }

  function updatePane(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
    if (node.type === 'pane' && node.id === sourcePaneId) {
      return { ...node, tabs: newTabs, activeTabId: newActiveTabId };
    }
    if (node.type === 'split') {
      return {
        ...node,
        children: [updatePane(node.children[0]), updatePane(node.children[1])] as [WorkspaceLayoutNode, WorkspaceLayoutNode],
      };
    }
    return node;
  }

  const newLayout: WorkspaceLayout = {
    ...layout,
    root: updatePane(layout.root),
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}

export function updateSplitSizes(
  layout: WorkspaceLayout,
  splitId: string,
  sizes: [number, number]
): LayoutMutationResult {
  const [size1, size2] = sizes;
  const clampedSize1 = Math.max(MIN_PANE_SIZE_PCT, Math.min(100 - MIN_PANE_SIZE_PCT, size1));
  const clampedSize2 = 100 - clampedSize1;

  function updateSplit(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
    if (node.type === 'split') {
      if (node.id === splitId) {
        return { ...node, sizes: [clampedSize1, clampedSize2] };
      }
      return {
        ...node,
        children: [updateSplit(node.children[0]), updateSplit(node.children[1])],
      };
    }
    return node;
  }

  const newLayout: WorkspaceLayout = {
    ...layout,
    root: updateSplit(layout.root),
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}

export function setActivePane(layout: WorkspaceLayout, paneId: string): LayoutMutationResult {
  const allPanes = getAllPanes(layout.root);
  if (!allPanes.find(p => p.id === paneId)) {
    return { success: false, error: `Pane ${paneId} not found` };
  }

  return {
    success: true,
    layout: { ...layout, activePaneId: paneId },
  };
}

export function setActiveTab(layout: WorkspaceLayout, paneId: string, tabId: string): LayoutMutationResult {
  const targetPane = findPaneById(layout.root, paneId);
  if (!targetPane) {
    return { success: false, error: `Pane ${paneId} not found` };
  }

  if (!targetPane.tabs.some(t => t.id === tabId)) {
    return { success: false, error: `Tab ${tabId} not found in pane ${paneId}` };
  }

  function updatePane(node: WorkspaceLayoutNode): WorkspaceLayoutNode {
    if (node.type === 'pane' && node.id === paneId) {
      return { ...node, activeTabId: tabId };
    }
    if (node.type === 'split') {
      return {
        ...node,
        children: [updatePane(node.children[0]), updatePane(node.children[1])] as [WorkspaceLayoutNode, WorkspaceLayoutNode],
      };
    }
    return node;
  }

  const newLayout: WorkspaceLayout = {
    ...layout,
    activePaneId: paneId,
    root: updatePane(layout.root),
  };

  const error = validateLayout(newLayout);
  if (error) {
    return { success: false, error };
  }

  return { success: true, layout: newLayout };
}
